import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

const PAGE_SIZE = 20;

const CONV_COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'owner', label: 'Contacto' },
  { key: 'status', label: 'Estado' },
  { key: 'assigned', label: 'Asignado' },
  { key: 'assigned_id', label: 'ID Asignado' },
  { key: 'updated_at', label: 'Última actividad' },
  { key: 'created_at', label: 'Creación' },
  { key: 'typification_id', label: 'Tipificación' },
  { key: 'notas', label: 'Notas' },
];

const MSG_COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'conversation_id', label: 'ID Conv.' },
  { key: 'owner', label: 'Contacto' },
  { key: 'created_at', label: 'Fecha' },
  { key: 'send', label: 'Enviado por' },
  { key: 'type', label: 'Tipo' },
  { key: 'content', label: 'Contenido' },
  { key: 'status', label: 'Estado' },
  { key: 'media_url', label: 'URL Media' },
];

function toDateInput(d) {
  return d.toISOString().slice(0, 10);
}

function ConversationsAdmin({ token, roleId }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('conversations');
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  const [convData, setConvData] = useState([]);
  const [convTotal, setConvTotal] = useState(0);
  const [convPage, setConvPage] = useState(1);

  const [msgData, setMsgData] = useState([]);
  const [msgTotal, setMsgTotal] = useState(0);
  const [msgPage, setMsgPage] = useState(1);

  const [loading, setLoading] = useState(false);

  // Filtros de búsqueda (celular, estado de conversación, rango de fechas). Los
  // inputs guardan lo que el usuario escribe; `applied` solo cambia al presionar
  // "Buscar", que es lo único que dispara una búsqueda real en el servidor.
  const [fPhone, setFPhone] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [applied, setApplied] = useState({ phone: '', status: '', from: '', to: '' });

  const [exportOpen, setExportOpen] = useState(false);
  const [exportFrom, setExportFrom] = useState(
    toDateInput(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
  );
  const [exportTo, setExportTo] = useState(toDateInput(new Date()));
  const [exportFormat, setExportFormat] = useState('xlsx');

  useEffect(() => {
    if (roleId !== 1) return;
    fetch('/api/companies?page=1', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => {
        setCompanies(d.data || []);
        if (d.data?.length > 0) setSelectedCompanyId(String(d.data[0].id));
      });
  }, [roleId, token]);

  // Agrega los filtros aplicados a los params de la petición.
  const appendFilters = (params) => {
    if (applied.phone) params.set('phone', applied.phone);
    if (applied.status) params.set('status', applied.status);
    if (applied.from) params.set('from', applied.from);
    if (applied.to) params.set('to', applied.to);
  };

  useEffect(() => {
    if (roleId === 1 && !selectedCompanyId) return;
    setLoading(true);
    const params = new URLSearchParams({ page: convPage });
    if (roleId === 1 && selectedCompanyId) params.set('company_id', selectedCompanyId);
    appendFilters(params);
    fetch(`/api/export/conversations?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { data: [], total: 0 })
      .then(d => { setConvData(d.data || []); setConvTotal(d.total || 0); })
      .finally(() => setLoading(false));
  }, [convPage, selectedCompanyId, roleId, token, applied]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab !== 'messages') return;
    if (roleId === 1 && !selectedCompanyId) return;
    setLoading(true);
    const params = new URLSearchParams({ page: msgPage });
    if (roleId === 1 && selectedCompanyId) params.set('company_id', selectedCompanyId);
    appendFilters(params);
    fetch(`/api/export/messages?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { data: [], total: 0 })
      .then(d => { setMsgData(d.data || []); setMsgTotal(d.total || 0); })
      .finally(() => setLoading(false));
  }, [msgPage, selectedCompanyId, roleId, token, tab, applied]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Buscar": aplica los filtros (única acción que dispara una búsqueda real) y
  // vuelve a la página 1 en ambas pestañas.
  const handleSearch = () => {
    setApplied({ phone: fPhone.trim(), status: fStatus, from: fFrom, to: fTo });
    setConvPage(1);
    setMsgPage(1);
  };

  const handleClearFilters = () => {
    setFPhone(''); setFStatus(''); setFFrom(''); setFTo('');
    setApplied({ phone: '', status: '', from: '', to: '' });
    setConvPage(1);
    setMsgPage(1);
  };

  const handleDownload = () => {
    const base = tab === 'conversations'
      ? '/api/export/conversations/download'
      : '/api/export/messages/download';
    const params = new URLSearchParams({ from: exportFrom, to: exportTo, format: exportFormat, token });
    if (roleId === 1 && selectedCompanyId) params.set('company_id', selectedCompanyId);
    window.location.href = `${base}?${params.toString()}`;
  };

  const data = tab === 'conversations' ? convData : msgData;
  const total = tab === 'conversations' ? convTotal : msgTotal;
  const page = tab === 'conversations' ? convPage : msgPage;
  const setPage = tab === 'conversations' ? setConvPage : setMsgPage;
  const columns = tab === 'conversations' ? CONV_COLUMNS : MSG_COLUMNS;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const inputStyle = {
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px',
    color: theme.colors.text,
    padding: '5px 8px',
    fontSize: '0.88rem',
  };

  return (
    <main style={{ padding: isMobile ? '1.25rem 1rem' : '2rem 1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: '14px',
        border: `1px solid ${theme.colors.border}`,
        padding: isMobile ? '1.25rem' : '2rem',
        boxShadow: `0 4px 24px ${theme.colors.glowSubtle}`,
      }}>
        <h1 style={{ color: theme.colors.primary, fontSize: '1.4rem', marginBottom: '1.5rem' }}>
          Conversaciones y Mensajes
        </h1>

        {roleId === 1 && (
          <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
            <label style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Empresa:</label>
            <select
              value={selectedCompanyId}
              onChange={e => {
                setSelectedCompanyId(e.target.value);
                setConvPage(1);
                setMsgPage(1);
              }}
              style={{
                backgroundColor: theme.colors.background,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '6px',
                color: theme.colors.text,
                padding: '6px 10px',
                fontSize: '0.9rem',
              }}
            >
              {companies.map(c => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Barra de búsqueda / filtros. Solo busca al presionar "Buscar". Aplica a
            ambas pestañas (conversaciones y mensajes). */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'flex-end',
          backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`,
          borderRadius: '10px', padding: '0.9rem 1rem', marginBottom: '1rem',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: isMobile ? '1 1 100%' : '1 1 160px', minWidth: 0 }}>
            <label style={{ color: theme.colors.textMuted, fontSize: '0.78rem' }}>Celular</label>
            <input
              type="text" value={fPhone} onChange={e => setFPhone(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
              placeholder="Ej: 573001234567"
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
            <label style={{ color: theme.colors.textMuted, fontSize: '0.78rem' }}>Estado</label>
            <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="">Todos</option>
              <option value="open">Abierta</option>
              <option value="closed">Cerrada</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: isMobile ? '1 1 46%' : '0 0 auto' }}>
            <label style={{ color: theme.colors.textMuted, fontSize: '0.78rem' }}>Desde</label>
            <input type="date" value={fFrom} onChange={e => setFFrom(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: isMobile ? '1 1 46%' : '0 0 auto' }}>
            <label style={{ color: theme.colors.textMuted, fontSize: '0.78rem' }}>Hasta</label>
            <input type="date" value={fTo} onChange={e => setFTo(e.target.value)} style={inputStyle} />
          </div>
          <button
            onClick={handleSearch}
            style={{
              padding: '7px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer',
              fontSize: '0.88rem', fontWeight: 700,
              backgroundColor: theme.colors.primary, color: theme.colors.background,
            }}
          >
            Buscar
          </button>
          {(applied.phone || applied.status || applied.from || applied.to) && (
            <button
              onClick={handleClearFilters}
              style={{
                padding: '7px 14px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`,
                cursor: 'pointer', fontSize: '0.85rem', backgroundColor: 'transparent', color: theme.colors.textMuted,
              }}
            >
              Limpiar
            </button>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? '0.75rem' : 0 }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {['conversations', 'messages'].map(t => (
              <button
                key={t}
                onClick={() => { setTab(t); setExportOpen(false); }}
                style={{
                  padding: '8px 18px',
                  borderRadius: '7px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: tab === t ? '700' : '400',
                  backgroundColor: tab === t ? theme.colors.primary : theme.colors.background,
                  color: tab === t ? theme.colors.background : theme.colors.text,
                }}
              >
                {t === 'conversations' ? 'Conversaciones' : 'Mensajes'}
              </button>
            ))}
          </div>
          <button
            onClick={() => setExportOpen(o => !o)}
            style={{
              padding: '8px 18px',
              borderRadius: '7px',
              border: `1px solid ${theme.colors.primary}`,
              cursor: 'pointer',
              fontSize: '0.9rem',
              backgroundColor: 'transparent',
              color: theme.colors.primary,
            }}
          >
            Exportar {exportOpen ? '▲' : '▼'}
          </button>
        </div>

        {exportOpen && (
          <div style={{
            backgroundColor: theme.colors.background,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: '10px',
            padding: '1rem 1.5rem',
            marginBottom: '1rem',
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Desde:</label>
              <input type="date" value={exportFrom} onChange={e => setExportFrom(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Hasta:</label>
              <input type="date" value={exportTo} onChange={e => setExportTo(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display: 'flex' }}>
              {['xlsx', 'csv'].map((f, i) => (
                <button
                  key={f}
                  onClick={() => setExportFormat(f)}
                  style={{
                    padding: '6px 14px',
                    border: `1px solid ${theme.colors.primary}`,
                    borderRadius: i === 0 ? '6px 0 0 6px' : '0 6px 6px 0',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: exportFormat === f ? '700' : '400',
                    backgroundColor: exportFormat === f ? theme.colors.primary : 'transparent',
                    color: exportFormat === f ? theme.colors.background : theme.colors.primary,
                  }}
                >
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
            <button
              onClick={handleDownload}
              style={{
                padding: '6px 20px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '700',
                backgroundColor: theme.colors.primary,
                color: theme.colors.background,
              }}
            >
              Descargar
            </button>
          </div>
        )}

        {loading ? (
          <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>Cargando...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  {columns.map(col => (
                    <th key={col.key} style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderBottom: `1px solid ${theme.colors.border}`,
                      color: theme.colors.primary,
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                    }}>
                      {col.label}
                    </th>
                  ))}
                  {tab === 'conversations' && (
                    <th style={{ padding: '10px 12px', borderBottom: `1px solid ${theme.colors.border}` }} />
                  )}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + (tab === 'conversations' ? 1 : 0)} style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textMuted }}>
                      Sin registros
                    </td>
                  </tr>
                ) : data.map((row, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                    {columns.map(col => (
                      <td key={col.key} style={{
                        padding: '9px 12px',
                        color: theme.colors.text,
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {col.key === 'status' ? (
                          <span style={{
                            fontSize: '0.75rem', fontWeight: 600, padding: '2px 9px', borderRadius: '10px',
                            backgroundColor: row.status === 'open' ? 'rgba(34,197,94,0.15)' : 'rgba(107,114,128,0.15)',
                            color: row.status === 'open' ? '#22c55e' : '#9ca3af',
                          }}>
                            {row.status === 'open' ? 'Abierta' : 'Cerrada'}
                          </span>
                        ) : (row[col.key] ?? '—')}
                      </td>
                    ))}
                    {tab === 'conversations' && (
                      <td style={{ padding: '9px 12px' }}>
                        <button
                          onClick={() => navigate(`/conversaciones/${row.id}/ver`)}
                          style={{
                            background: 'none', border: `1px solid ${theme.colors.primary}`,
                            color: theme.colors.primary, padding: '3px 12px',
                            borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                          }}
                        >
                          Observar
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.2rem' }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '6px 16px',
              borderRadius: '6px',
              border: `1px solid ${theme.colors.border}`,
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              backgroundColor: 'transparent',
              color: page === 1 ? theme.colors.textMuted : theme.colors.text,
              fontSize: '0.88rem',
            }}
          >
            ← Anterior
          </button>
          <span style={{ color: theme.colors.textMuted, fontSize: '0.88rem' }}>
            Página {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '6px 16px',
              borderRadius: '6px',
              border: `1px solid ${theme.colors.border}`,
              cursor: page === totalPages ? 'not-allowed' : 'pointer',
              backgroundColor: 'transparent',
              color: page === totalPages ? theme.colors.textMuted : theme.colors.text,
              fontSize: '0.88rem',
            }}
          >
            Siguiente →
          </button>
        </div>
      </div>
    </main>
  );
}

export default ConversationsAdmin;
